import { uiText } from "@/lib/i18n/uiText";
import { PREDICTIONS_READ } from "@/lib/predictionColumns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2 } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { buildFixtureSlug } from "@/lib/entities";
import { Badge } from "@/components/ui/badge";
import {
  groupResults,
  searchFacets,
  searchSiteIndex,
  hasActiveFilters,
  type SearchEntry,
  type SearchFilters,
} from "@/lib/siteSearch";

interface PredictionRow {
  id: string;
  team_home: string | null;
  team_away: string | null;
  league: string | null;
  market_type: string | null;
  predicted_outcome: string | null;
  kickoff_time: string | null;
}

/**
 * Live fixture/prediction matches, on top of the static page index.
 * Facets narrow the query too: a league filter constrains `league`, and a
 * non-football sport filter suppresses football fixtures entirely.
 */
function usePredictionSearch(query: string, filters: SearchFilters) {
  const q = query.trim();
  const leagueLabel = filters.leagueLabel ?? "";
  const sportBlocked = !!filters.sport && filters.sport !== "football";
  const typeBlocked =
    !!filters.contentType && filters.contentType !== "Fixtures" && filters.contentType !== "Predictions";

  return useQuery({
    queryKey: ["site-search-predictions", q.toLowerCase(), leagueLabel, filters.sport ?? "", filters.contentType ?? ""],
    enabled: q.length >= 2 && !sportBlocked && !typeBlocked,
    staleTime: 60_000,
    queryFn: async (): Promise<PredictionRow[]> => {
      const pattern = `%${q.replace(/[%_,]/g, " ").trim()}%`;
      let builder = supabase
        .from(PREDICTIONS_READ)
        .select("id, team_home, team_away, league, market_type, predicted_outcome, kickoff_time")
        .eq("status", "approved")
        .or(
          [
            `team_home.ilike.${pattern}`,
            `team_away.ilike.${pattern}`,
            `league.ilike.${pattern}`,
            `market_type.ilike.${pattern}`,
            `predicted_outcome.ilike.${pattern}`,
          ].join(","),
        );

      if (leagueLabel) {
        builder = builder.ilike("league", `%${leagueLabel.replace(/ Predictions$/i, "").trim()}%`);
      }

      const { data, error } = await builder.order("kickoff_time", { ascending: false }).limit(12);
      if (error) return [];
      return (data ?? []) as PredictionRow[];
    },
  });
}


interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Site-wide search. Case- and accent-insensitive, multi-term, and covers every
 * public surface: prediction hubs, leagues/competitions, markets, other sports,
 * guides, news/blog, country hubs, static pages and live fixtures.
 *
 * Admin surfaces are excluded — the static index filters admin routes and the
 * live query only reads approved public predictions.
 */
export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});

  const facets = useMemo(() => searchFacets(filters), [filters]);
  const { data: predictions = [], isFetching } = usePredictionSearch(query, filters);

  const staticResults = useMemo(
    () => searchSiteIndex(query, { ...filters, limit: 60 }),
    [query, filters],
  );
  const grouped = useMemo(() => groupResults(staticResults), [staticResults]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setFilters({});
    }
  }, [open]);

  const go = (url: string) => {
    onOpenChange(false);
    navigate(url);
  };

  const select = (entry: SearchEntry) => go(entry.url);

  const hasResults = staticResults.length > 0 || predictions.length > 0;
  const filtersActive = hasActiveFilters(filters);

  const selectClass =
    "h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground";

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} commandProps={{ shouldFilter: false }}>
      {/* shouldFilter is off in the underlying Command: we rank ourselves so
          the match is accent-insensitive and spans keywords + live data. */}
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={uiText("Search predictions, leagues, markets, guides, news…")}
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <label className="sr-only" htmlFor="search-filter-type">
           {uiText("Filter by content type")} </label>
        <select
          id="search-filter-type"
          className={selectClass}
          value={filters.contentType ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              contentType: (e.target.value || undefined) as SearchFilters["contentType"],
            }))
          }
        >
          <option value="">{uiText("All types")}</option>
          {facets.contentTypes.map((c) => (
            <option key={c.value} value={c.value}>
              {uiText(c.label)} ({c.count})
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="search-filter-sport">
           {uiText("Filter by sport")} </label>
        <select
          id="search-filter-sport"
          className={selectClass}
          value={filters.sport ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              sport: e.target.value || undefined,
              league: undefined,
              leagueLabel: undefined,
              competition: undefined,
            }))
          }
        >
          <option value="">{uiText("All sports")}</option>
          {facets.sports.map((s) => (
            <option key={s.value} value={s.value}>
              {uiText(s.label)} ({s.count})
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="search-filter-league">
           {uiText("Filter by league")} </label>
        <select
          id="search-filter-league"
          className={selectClass}
          value={filters.league ?? ""}
          onChange={(e) => {
            const value = e.target.value || undefined;
            const label = facets.leagues.find((l) => l.value === value)?.label;
            setFilters((f) => ({ ...f, league: value, leagueLabel: label }));
          }}
        >
          <option value="">{uiText("All leagues")}</option>
          {facets.leagues.map((l) => (
            <option key={l.value} value={l.value}>
              {uiText(l.label)} ({l.count})
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="search-filter-competition">
           {uiText("Filter by competition")} </label>
        <select
          id="search-filter-competition"
          className={selectClass}
          value={filters.competition ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, competition: e.target.value || undefined }))}
        >
          <option value="">{uiText("All competitions")}</option>
          {facets.competitions.map((c) => (
            <option key={c.value} value={c.value}>
              {uiText(c.label)} ({c.count})
            </option>
          ))}
        </select>

        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => setFilters({})}
          >
             {uiText("Clear filters")} </Button>
        )}
        {filtersActive && (
          <Badge variant="secondary" className="text-[10px]">
            {staticResults.length}  {uiText("filtered")} </Badge>
        )}
      </div>

      <CommandList>
        {!query.trim() && !filtersActive && (

          <CommandEmpty>{uiText("Start typing to search the whole site.")}</CommandEmpty>
        )}
        {(query.trim() || filtersActive) && !hasResults && !isFetching && (
          <CommandEmpty>
            {query.trim() ? uiText("No matches for “{{v0}}”.", { v0: query }) : uiText("No pages match these filters.")}
          </CommandEmpty>
        )}


        {predictions.length > 0 && (
          <CommandGroup heading="Fixtures & predictions">
            {predictions.map((p) => {
              const slug = buildFixtureSlug(p.team_home ?? "", p.team_away ?? "", p.kickoff_time);
              return (
                <CommandItem key={p.id} value={`fixture-${p.id}`} onSelect={() => go(`/fixtures/${slug}`)}>
                  <div className="flex flex-col">
                    <span>
                      {p.team_home}  {uiText("vs")} {p.team_away}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[p.league, p.market_type, p.predicted_outcome].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {grouped.map(([group, entries], idx) => (
          <div key={group}>
            {(idx > 0 || predictions.length > 0) && <CommandSeparator />}
            <CommandGroup heading={group}>
              {entries.map((entry) => (
                <CommandItem key={entry.id} value={entry.id} onSelect={() => select(entry)}>
                  <div className="flex flex-col">
                    <span>{uiText(entry.title)}</span>
                    {entry.subtitle && (
                      <span className="line-clamp-1 text-xs text-muted-foreground">{uiText(entry.subtitle)}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        ))}

        {isFetching && (
          <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />  {uiText("Searching fixtures…")} </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Header trigger + global keyboard shortcuts (⌘K / Ctrl+K, and "/" when not
 * typing in a field).
 */
export function GlobalSearchTrigger() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={uiText("Search Statlign")}
        className="h-9 gap-2 px-2 text-muted-foreground sm:px-3"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">{uiText("Search…")}</span>
        <kbd className="hidden rounded border border-border px-1 text-[10px] lg:inline">⌘K</kbd>
      </Button>
      <GlobalSearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
